import axios from 'axios'
import { format, parseISO } from 'date-fns'
import { TFunction } from 'i18next'

import { BookingDto, ReceiptDto } from './types'

export const checkPrinterAlive = async (ip: string): Promise<boolean> => {
  try {
    const url = `https://${ip}/cgi-bin/epos/service.cgi`
    const response = await axios.get(url, { timeout: 3000 })
    return response.status === 200
  } catch {
    return false
  }
}

type LessonGroup = {
  lessonId: string
  productName: string
  date: string
  startTime: string
  endTime: string
  bookings: BookingDto[]
}

const groupBookingsByLesson = (bookings: BookingDto[]): LessonGroup[] => {
  const map = new Map<string, LessonGroup>()
  for (const b of bookings) {
    const existing = map.get(b.lessonId)
    if (existing) {
      existing.bookings.push(b)
    } else {
      map.set(b.lessonId, {
        lessonId: b.lessonId,
        productName: (b as any).lesson?.product?.name ?? '',
        date: (b as any).lesson?.date ?? '',
        startTime: (b as any).lesson?.startTime ?? '',
        endTime: (b as any).lesson?.endTime ?? '',
        bookings: [b],
      })
    }
  }
  return [...map.values()]
}

const escapeXml = (str: string): string => str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

export const buildReceiptXml = (receipt: ReceiptDto, t: TFunction): string => {
  const groups = groupBookingsByLesson(receipt.bookings)
  const totalBase = receipt.bookings.reduce((sum, b) => sum + Number(b.basePrice), 0)
  const totalVat = receipt.bookings.reduce((sum, b) => sum + Number(b.vat), 0)
  const totalPrice = receipt.bookings.reduce((sum, b) => sum + Number(b.price), 0)
  const createdAt = format(parseISO(receipt.createdAt), 'dd.MM.yyyy HH:mm')

  const lessonSections = groups
    .map((group) => {
      const dateStr = group.date ? format(parseISO(group.date), 'dd.MM.') : ''
      const header = `${escapeXml(group.productName)} ${group.startTime}-${group.endTime} ${dateStr}`
      const bookingLines = group.bookings
        .map((b) => {
          const name = escapeXml(b.skierName)
          const price = Number(b.price).toFixed(2)
          return `      <text>  ${name}  ${price}&#10;</text>`
        })
        .join('\n')

      return `      <text em="true">${header}&#10;</text>
${bookingLines}
      <text>&#10;</text>`
    })
    .join('\n')

  return `<?xml version="1.0" encoding="utf-8"?>
<s:Envelope xmlns:s="http://schemas.xmlsoap.org/soap/envelope/">
  <s:Body>
    <epos-print xmlns="http://www.epson-pos.com/schemas/2011/03/epos-print">
      <text align="center"/>
      <text width="2" height="2"/>
      <text font="font_a"/>
      <text>${escapeXml(t('print.receipt.header'))}&#10;</text>
      <text width="1" height="1"/>
      <text font="font_b"/>
      <text>${escapeXml(t('print.receipt.receiptNumber', { number: receipt.receiptNumber }))}&#10;</text>
      <text>${createdAt}&#10;</text>
      <text>${escapeXml(receipt.customer?.name ?? '')}&#10;</text>
      <text>&#10;</text>
      <text align="left"/>
${lessonSections}
      <text>--------------------------------&#10;</text>
      <text>  ${escapeXml(t('print.receipt.subtotal'))}  ${totalBase.toFixed(2)}&#10;</text>
      <text>  ${escapeXml(t('print.receipt.vat'))}  ${totalVat.toFixed(2)}&#10;</text>
      <text font="font_a"/>
      <text width="2" height="2"/>
      <text align="center"/>
      <text>${escapeXml(t('print.receipt.total'))} ${totalPrice.toFixed(2)}&#10;</text>
      <text width="1" height="1"/>
      <text font="font_b"/>
      <text align="left"/>
      <text>  ${escapeXml(t('print.receipt.payment'))}: ${escapeXml(t('paymentType.' + receipt.paymentType))}&#10;</text>
      <text>  ${escapeXml(t('print.receipt.posId'))}: ${escapeXml(receipt.posId)}&#10;</text>
      <feed line="3"/>
      <cut type="feed"/>
    </epos-print>
  </s:Body>
</s:Envelope>`
}

export const printReceipt = async (ip: string, receipt: ReceiptDto, t: TFunction): Promise<void> => {
  const xml = buildReceiptXml(receipt, t)
  await axios.post(`https://${ip}/cgi-bin/epos/service.cgi?devid=local_printer&timeout=5000`, xml, {
    headers: {
      'Content-Type': 'text/xml; charset=utf-8',
    },
  })
}
